import { useEffect,useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { cn } from "@/lib/utils";

import { ThinkingBox } from "./ThinkingBox";

export interface Message {
  id: string;
  content: string;
  thinking?: string;
  role: "user" | "assistant";
  timestamp: Date;
  attachmentCount?: number; // Number of files/images attached
}

interface ChatMessageProps {
  message: Message;
  isStreaming?: boolean;
  hasReceivedTokens?: boolean;
}

export const ChatMessage = ({ message, isStreaming = false, hasReceivedTokens = false }: ChatMessageProps) => {
  const isUser = message.role === "user";
  const [isClosing, setIsClosing] = useState(false);
  const [renderThinkingBox, setRenderThinkingBox] = useState(false);

  // Convert literal \n to actual newlines for markdown
  const formattedContent = message.content.replace(/\\n/g, "\n");
  
  // Add attachment label if there are attachments
  const attachmentLabel =
    message.attachmentCount && message.attachmentCount > 0
      ? ` [${message.attachmentCount} file${message.attachmentCount > 1 ? "s" : ""} attached]`
      : "";

  // Show thinking box while streaming, hide only when message is complete
  const shouldShowThinkingBox = !isUser && isStreaming;

  // Handle thinking box visibility with closing animation
  useEffect(() => {
    if (shouldShowThinkingBox) {
      setRenderThinkingBox(true);
      setIsClosing(false);
    } else if (renderThinkingBox && !isClosing) {
      // Start closing animation
      setIsClosing(true);
      // Remove from DOM after animation completes
      const timer = setTimeout(() => {
        setRenderThinkingBox(false);
        setIsClosing(false);
      }, 300); // Match animation duration
      return () => clearTimeout(timer);
    }
  }, [shouldShowThinkingBox, renderThinkingBox, isClosing]);

  return (
    <div
      className={cn("flex w-full", isUser ? "justify-end" : "justify-start")}
    >
      <div className="max-w-[85%] min-w-0 flex flex-col gap-0">
        {/* Thinking box as loader - only show when no text content yet */}
        {renderThinkingBox && (
          <div 
            className={cn(
              "transition-all duration-300 overflow-hidden",
              isClosing 
                ? "opacity-0 max-h-0 mb-0" 
                : "opacity-100 max-h-96 mb-2"
            )}
          >
            <ThinkingBox 
              thinking={message.thinking || ""} 
              isStreaming={isStreaming}
              hasStartedStreaming={hasReceivedTokens}
            />
          </div>
        )}

        {/* Main message content - only show if there's content */}
        {formattedContent && (
          <div
            className={cn(
              "rounded-lg p-3 border overflow-hidden",
              isUser
                ? "bg-sds-purple-20 border-sds-purple-40 rounded-br-none"
                : "bg-sds-neutral-00 border-sds-neutral-40 rounded-bl-none",
            )}
          >
            <div
              className={cn(
                "prose prose-sm max-w-none overflow-x-auto",
                isUser ? "text-sds-text-black" : "text-sds-neutral-100",
                // Markdown styles
                "[&>*:first-child]:mt-0 [&>*:last-child]:mb-0",
                "[&_p]:my-2 [&_p]:leading-relaxed",
                "[&_strong]:font-semibold [&_strong]:text-sds-text-black",
                "[&_ul]:my-2 [&_ul]:ml-4 [&_ul]:list-disc",
                "[&_ol]:my-2 [&_ol]:ml-4 [&_ol]:list-decimal",
                "[&_li]:my-1",
                "[&_h1]:text-lg [&_h1]:font-semibold [&_h1]:my-3",
                "[&_h2]:text-base [&_h2]:font-semibold [&_h2]:my-2",
                "[&_h3]:text-sm [&_h3]:font-semibold [&_h3]:my-2",
                "[&_code]:bg-sds-neutral-20 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-sm",
                "[&_pre]:bg-sds-neutral-20 [&_pre]:p-2 [&_pre]:rounded [&_pre]:overflow-x-auto",
                // Link styles
                "[&_a]:text-sds-purple-100 [&_a]:underline [&_a]:font-medium [&_a]:cursor-pointer",
                "hover:[&_a]:text-sds-purple-80",
                // Table styles - min-w-max allows tables to be scrollable when too wide
                "[&_table]:border-collapse [&_table]:my-3 [&_table]:min-w-max",
                "[&_table]:text-sm",
                "[&_thead]:bg-sds-neutral-20",
                "[&_th]:border [&_th]:border-sds-neutral-40 [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:font-semibold",
                "[&_td]:border [&_td]:border-sds-neutral-40 [&_td]:px-3 [&_td]:py-2",
                "[&_tbody_tr:hover]:bg-sds-neutral-10",
              )}
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{formattedContent}</ReactMarkdown>
              {attachmentLabel && (
                <div className="text-xs text-sds-neutral-60 mt-1 italic">
                  {attachmentLabel}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
